import { cloneDeep, has, isEmpty, isNull, isNumber, isUndefined, memoize, pick } from 'lodash'
import { computed, nextTick, onMounted, ref, shallowRef, toValue, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

import CheckinTemplatesApiHandler from '@/api/checkin-templates'
import ObjectivesInfoApiHandler from '@/api/okr-elements'
import { useSettingsBackButton } from '@/composables/settings-back-button'
import { ROUTE_NAMES } from '@/routes/route-helpers'
import { EVENT_NAMES } from '@/tracking/gtm-helpers'
import { gtmTracker } from '@/tracking/gtm-tracking'
import { updateCommentReactions } from '@/utils/comments'
import {
  CHECKIN_ENTITY_KEYS,
  CHECKIN_TEMPLATE_ENTITY_KEYS,
  COMMENT_ENTITY_KEYS
} from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { isKR, OBJECTIVE_TYPES } from '@/utils/objective-types'
import {
  currentUserCanUpdateObjective,
  OKR_STATUSES,
  OKR_STATUSES_DISABLED
} from '@/utils/objectives'
import { APP_ENTITIES_PERMISSIONS } from '@/utils/permissions'
import { getTimeAgo } from '@/utils/time-ago'
import { OKR_ELEMENT_KEYS } from '@/utils/view-row-actions'
import { usePlansLimitations } from '@/utils/web-app/plans-limitations'

export const CHECKIN_DRAFT_MAP_LS_KEY = 'checkin_draft_map'
export const CHECKIN_DRAFT_MAP_SIZE_LIMIT = 200
const CHECKIN_DRAFT_ITEM_LS_KEY_PREFIX = 'checkin_draft_item_'
export const getCheckinDraftItemLsKey = id => `${CHECKIN_DRAFT_ITEM_LS_KEY_PREFIX}${id}`

export const CHECKIN_FIELDS_KEYS = {
  VALUE_CHANGE: 'valueChange',
  PROGRESS_CHANGE: 'progressChange',
  STATUS_CHANGE: 'statusChange'
}

export const CHECKIN_FIELDS_IDS = {
  VALUE: 'value',
  PROGRESS: 'progress',
  STATUS: 'status',
  COMMENT: 'comment'
}

export const CHECKIN_DATE_FORMAT = 'MMMM DD, YYYY, h:mm A'
export const CHECKIN_DATE_FORMAT_SHORT = 'MMM D, h:mm A'

const { ID, CREATE_LABEL, VIEW_LABEL } = CHECKIN_ENTITY_KEYS

export const CHECKIN_FIELDS = {
  [CHECKIN_FIELDS_KEYS.VALUE_CHANGE]: {
    [ID]: CHECKIN_FIELDS_IDS.VALUE,
    [VIEW_LABEL]: 'checkin_field.view.value',
    [CREATE_LABEL]: 'checkin_field.create.value'
  },
  [CHECKIN_FIELDS_KEYS.PROGRESS_CHANGE]: {
    [ID]: CHECKIN_FIELDS_IDS.PROGRESS,
    [VIEW_LABEL]: 'checkin_filed.view.progress',
    [CREATE_LABEL]: 'checkin_filed.view.progress'
  },
  [CHECKIN_FIELDS_KEYS.STATUS_CHANGE]: {
    [ID]: CHECKIN_FIELDS_IDS.STATUS,
    [VIEW_LABEL]: 'checkin_field.view.status',
    [CREATE_LABEL]: 'checkin_field.create.status'
  }
}

export const getCheckinNecessaryFieldsSet = ({
  typeId = OBJECTIVE_TYPES.KR,
  existContributeChildren = false
} = {}) => {
  const fields = cloneDeep(CHECKIN_FIELDS)

  if (existContributeChildren) {
    delete fields[CHECKIN_FIELDS_KEYS.VALUE_CHANGE]
  }

  if (isKR({ typeId })) {
    return fields
  }

  return pick(fields, [CHECKIN_FIELDS_KEYS.PROGRESS_CHANGE, CHECKIN_FIELDS_KEYS.STATUS_CHANGE])
}

export const CHECKIN_PREVIEW_NAVIGATION_BUTTONS = {
  nextEl: '.cpi-Button-next',
  prevEl: '.cpi-Button-prev'
}

export const CHECKINS_PREVIEW_LIMIT = 20
export const CHECKINS_FEED_LIMIT = 50

export const useCloseElement = ({
  localConfidenceLevelId = OKR_STATUSES.AUTO,
  elementId,
  childCount = 0
} = {}) => {
  const isClosedStatusSelected = computed(() => {
    const selectedConfidenceLevelId = toValue(localConfidenceLevelId)
    return OKR_STATUSES_DISABLED.includes(selectedConfidenceLevelId)
  })

  const userHasPermissionToCloseNestedElements = ref(undefined)

  const isShowCloseNestedElementsCheckbox = computed(() => {
    return (
      userHasPermissionToCloseNestedElements.value &&
      isClosedStatusSelected.value &&
      Boolean(toValue(childCount))
    )
  })

  const handleCloseNestedElementsCheckbox = async () => {
    if (!isUndefined(userHasPermissionToCloseNestedElements.value)) return

    if (!isClosedStatusSelected.value) return

    if (!toValue(childCount)) return

    const selectedConfidenceLevelId = toValue(localConfidenceLevelId)

    const api = new ObjectivesInfoApiHandler()
    try {
      const { allChildHasPermission } = await api.checkNestedElementsPermission({
        elementId: toValue(elementId),
        [OKR_ELEMENT_KEYS.CONFIDENCE_LEVEL_ID]: selectedConfidenceLevelId
      })
      userHasPermissionToCloseNestedElements.value = allChildHasPermission
    } catch (error) {
      handleError({ error })
    }
  }

  const destroyCloseElementState = () => {
    userHasPermissionToCloseNestedElements.value = undefined
  }

  return {
    isShowCloseNestedElementsCheckbox,
    handleCloseNestedElementsCheckbox,
    destroyCloseElementState
  }
}

export const useCheckinValueField = ({ okrElement } = {}) => {
  const hasContributeChildren = computed(() => {
    return toValue(okrElement)[OKR_ELEMENT_KEYS.EXIST_CONTRIBUTE_CHILDREN]
  })

  const { dataSourceLimitations } = usePlansLimitations()
  const isValueFieldDisabledByDataSourceQuery = computed(() => {
    return (
      isKR(toValue(okrElement)) &&
      !isEmpty(toValue(okrElement)[OKR_ELEMENT_KEYS.DATA_SOURCE_QUERY]) &&
      dataSourceLimitations.value.isAvailable
    )
  })

  const isValueFieldNeeded = computed(() => {
    return isKR(toValue(okrElement)) && !hasContributeChildren.value
  })

  return {
    isValueFieldNeeded,
    isValueFieldDisabledByDataSourceQuery,
    hasContributeChildren
  }
}

export const CHECKINS_PERMISSIONS = {
  READ: APP_ENTITIES_PERMISSIONS.READ,
  UPDATE: APP_ENTITIES_PERMISSIONS.UPDATE,
  REMOVE: APP_ENTITIES_PERMISSIONS.REMOVE
}

const permissionsCacheKeyResolver = checkin => {
  if (isEmpty(checkin)) {
    return 'no-permissions'
  }

  return checkin.permissions.join('|')
}

export const userCanReadCheckin = memoize(checkin => {
  if (isEmpty(checkin)) {
    return false
  }

  return checkin.permissions.includes(CHECKINS_PERMISSIONS.READ)
}, permissionsCacheKeyResolver)

export const userCanUpdateCheckin = memoize(checkin => {
  if (isEmpty(checkin)) {
    return false
  }

  return checkin.permissions.includes(CHECKINS_PERMISSIONS.UPDATE)
}, permissionsCacheKeyResolver)

export const userCanDeleteCheckin = memoize(checkin => {
  if (isEmpty(checkin)) {
    return false
  }

  return checkin.permissions.includes(CHECKINS_PERMISSIONS.REMOVE)
}, permissionsCacheKeyResolver)

export const CHECKIN_MODAL_ENTRY_POINTS = {
  PREVIEW: 'checkin-preview',
  CREATE: 'checkin-create',
  PREVIEW_ONLY: 'checkin-preview-only',
  NO_ACCESS: 'checkin-no-access'
}

export const useCheckinModal = ({ okrElement = {}, entryPoint = '' } = {}) => {
  const isCheckinModalOpen = ref(false)
  const isShowCheckinModal = ref(false)

  const canCreateCheckinToElements = computed(() => {
    return currentUserCanUpdateObjective(toValue(okrElement))
  })

  const DEFAULT_CHECKIN_MODAL_LAUNCH_DATA = {
    entryPoint: CHECKIN_MODAL_ENTRY_POINTS.CREATE,
    okrElement: {},
    presetBackdate: null,
    overrideCurrentValue: undefined
  }

  const checkinModalLaunchData = shallowRef({
    ...DEFAULT_CHECKIN_MODAL_LAUNCH_DATA
  })

  const handleOpenCheckinModal = async (payload = {}) => {
    isShowCheckinModal.value = true
    checkinModalLaunchData.value.entryPoint = payload.entryPoint
    checkinModalLaunchData.value.okrElement = payload.okrElement
    checkinModalLaunchData.value.presetBackdate = payload.presetBackdate || null
    checkinModalLaunchData.value.overrideCurrentValue = payload.overrideCurrentValue

    await nextTick()
    requestAnimationFrame(() => {
      isCheckinModalOpen.value = true
    })
  }

  const onCheckinModalClosed = () => {
    checkinModalLaunchData.value = { ...DEFAULT_CHECKIN_MODAL_LAUNCH_DATA }

    isShowCheckinModal.value = false
  }

  const isLaunchAsPreview = computed(() => {
    return toValue(entryPoint) === CHECKIN_MODAL_ENTRY_POINTS.PREVIEW
  })

  const isLaunchAsPreviewOnly = computed(() => {
    return toValue(entryPoint) === CHECKIN_MODAL_ENTRY_POINTS.PREVIEW_ONLY
  })

  const isLaunchAsAnyPreviewMode = computed(() => {
    return isLaunchAsPreview.value || isLaunchAsPreviewOnly.value
  })

  return {
    isCheckinModalOpen,
    handleOpenCheckinModal,
    isShowCheckinModal,
    canCreateCheckinToElements,
    onCheckinModalClosed,
    checkinModalLaunchData,
    isLaunchAsPreview,
    isLaunchAsPreviewOnly,
    isLaunchAsAnyPreviewMode
  }
}

export const isCheckinHistoricValueExist = ({ parameter = {}, path = 'unknown' }) => {
  return has(parameter, path) && !isNull(parameter[path][CHECKIN_ENTITY_KEYS.VALUE])
}

export const getCheckinModalEntryPoint = ({ okrElement = {}, previewOnly = false } = {}) => {
  const { lastCheckin } = okrElement

  if (previewOnly) {
    return lastCheckin
      ? CHECKIN_MODAL_ENTRY_POINTS.PREVIEW_ONLY
      : CHECKIN_MODAL_ENTRY_POINTS.NO_ACCESS
  }

  return lastCheckin ? CHECKIN_MODAL_ENTRY_POINTS.PREVIEW : CHECKIN_MODAL_ENTRY_POINTS.CREATE
}

export const useLastCheckin = ({ lastCheckin = null } = {}) => {
  const { t } = useI18n()

  const lastCheckinRelativeDate = computed(() => {
    const checkin = toValue(lastCheckin)

    if (checkin) {
      return getTimeAgo(checkin.date)
    }

    return t('common.not_yet')
  })

  return {
    lastCheckinRelativeDate
  }
}

export const updateCheckinReplies = ({
  item = {},
  itemIndex = 0,
  replies = [],
  checkinsList
} = {}) => {
  const updateCheckinCommentsList = checkin => {
    const { comments = [] } = checkin

    if (isEmpty(comments)) return checkin

    const [firstComment] = comments

    return {
      ...checkin,
      [CHECKIN_ENTITY_KEYS.COMMENTS]: [
        {
          ...firstComment,
          [COMMENT_ENTITY_KEYS.CHILD_COMMENTS]: replies
        }
      ]
    }
  }

  const isItemByIndexExists =
    isNumber(itemIndex) &&
    checkinsList[itemIndex] &&
    checkinsList[itemIndex][CHECKIN_ENTITY_KEYS.ID] === item[CHECKIN_ENTITY_KEYS.ID]

  if (isItemByIndexExists) {
    checkinsList[itemIndex] = updateCheckinCommentsList(checkinsList[itemIndex])
  } else {
    const fallbackIndex = checkinsList.findIndex(
      checkin => checkin[CHECKIN_ENTITY_KEYS.ID] === item[CHECKIN_ENTITY_KEYS.ID]
    )

    if (fallbackIndex !== -1) {
      updateCheckinReplies({
        item,
        itemIndex: fallbackIndex,
        replies,
        checkinsList
      })
    }
  }
}

export const updateCheckinCommentReactions = ({
  checkinsList = [],
  checkinId,
  commentId,
  parentId,
  reactions
}) => {
  if (!checkinId) return

  const targetCheckin = checkinsList.find(checkin => checkin[ID] === checkinId)

  if (!targetCheckin) return

  updateCommentReactions({
    comments: targetCheckin[CHECKIN_ENTITY_KEYS.COMMENTS],
    commentId,
    parentId,
    reactions
  })
}

export const useCheckinTemplates = ({
  okrElement,
  checkinCommentContent = '',
  fetchTemplatesOnStart = true
} = {}) => {
  const store = useStore()
  const isPluginAdmin = computed(() => store.state.pluginOptions.isPluginAdmin)
  const { t } = useI18n()
  const router = useRouter()
  const { saveSettingsReferer } = useSettingsBackButton()

  const SAVE_AS_NEW = 'SAVE_AS_NEW'
  const MANAGE_TEMPLATES = 'MANAGE_TEMPLATES'

  const templates = ref([])

  const fetchTemplates = async () => {
    const api = new CheckinTemplatesApiHandler()

    try {
      templates.value = await api.getTemplates({
        workspaceIds: [toValue(okrElement)[OKR_ELEMENT_KEYS.WORKSPACE_ID]]
      })
    } catch (error) {
      handleError({ error })
    }
  }

  onMounted(() => {
    if (toValue(fetchTemplatesOnStart)) {
      fetchTemplates()
    }
  })

  const templatesActions = computed(() => {
    if (isPluginAdmin.value) {
      return [
        {
          id: SAVE_AS_NEW,
          title: t('checkins.save_as_new'),
          icon: 'plus-next'
        },
        {
          id: MANAGE_TEMPLATES,
          title: t('checkins.manage_templates'),
          icon: 'edit-next'
        }
      ]
    }

    return []
  })

  const bottomFixedItems = computed(() => {
    return templatesActions.value.map(item => item[CHECKIN_TEMPLATE_ENTITY_KEYS.ID])
  })

  const optionsList = computed(() => {
    return [...templatesActions.value, ...templates.value]
  })

  const getTemplateData = async templateId => {
    const api = new CheckinTemplatesApiHandler()

    try {
      return await api.getTemplateById(templateId)
    } catch (error) {
      handleError({ error })
      return null
    }
  }

  const templateModalState = ref({
    isShow: false,
    templateContent: ''
  })

  const closeTemplateModal = () => {
    templateModalState.value.isShow = false
    templateModalState.value.templateContent = ''
  }

  const descriptionFieldReferenceKey = 'description-field'
  const descriptionFieldReference = useTemplateRef(descriptionFieldReferenceKey)

  const onTemplateSelect = async actionId => {
    if (actionId === SAVE_AS_NEW) {
      templateModalState.value.templateContent = toValue(checkinCommentContent)
      templateModalState.value.isShow = true
      return
    }

    if (actionId === MANAGE_TEMPLATES) {
      saveSettingsReferer()
      router.push({ name: ROUTE_NAMES.SETTINGS_CHECK_INS_TEMPLATES })
      return
    }

    const templateData = await getTemplateData(actionId)

    if (templateData) {
      descriptionFieldReference.value.addExternalContent(
        templateData[CHECKIN_TEMPLATE_ENTITY_KEYS.TEXT] || ''
      )

      gtmTracker.logEvent(EVENT_NAMES.CHECKIN_TEMPLATE_APPLIED)
    }
  }

  return {
    templatesActions,
    bottomFixedItems,
    optionsList,
    getTemplateData,
    onTemplateSelect,
    templateModalState,
    descriptionFieldReferenceKey,
    closeTemplateModal,
    fetchTemplates
  }
}
